import React, { createContext, useContext, useEffect, useState } from 'react';

interface ReCaptchaContextType {
  isLoaded: boolean;
  executeRecaptcha: (action: string) => Promise<string>;
}

const ReCaptchaContext = createContext<ReCaptchaContextType | undefined>(undefined);

export const useReCaptchaContext = () => {
  const context = useContext(ReCaptchaContext);
  if (!context) {
    throw new Error('useReCaptchaContext must be used within ReCaptchaProvider');
  }
  return context;
};

interface ReCaptchaProviderProps {
  children: React.ReactNode;
  siteKey?: string;
}

export const ReCaptchaProvider: React.FC<ReCaptchaProviderProps> = ({ 
  children,
  siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    // Skip if no site key configured
    if (!siteKey) {
      console.warn('reCAPTCHA site key not configured. Bot protection will be disabled.');
      setIsLoaded(true);
      return;
    }

    // Check if script already loaded
    if (window.grecaptcha) {
      setIsLoaded(true);
      return;
    }

    // Load reCAPTCHA script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      let loaded = false;
      
      // Wait for grecaptcha to be ready with integrated timeout
      let checkCount = 0;
      const checkReady = setInterval(() => {
        if (window.grecaptcha?.ready && !loaded) {
          clearInterval(checkReady);
          window.grecaptcha.ready(() => {
            loaded = true;
            setIsLoaded(true);
          });
        }
        checkCount++;
        if (checkCount >= 100) { // 100 checks * 100ms = 10 seconds
          clearInterval(checkReady);
          if (!loaded) {
            setLoadError('reCAPTCHA script loading timed out');
            console.error('reCAPTCHA failed to load within timeout');
          }
        }
      }, 100);
    };

    script.onerror = () => {
      setLoadError('Failed to load reCAPTCHA script');
      console.error('Failed to load reCAPTCHA script. Bot protection will be disabled.');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount
      const existingScript = document.querySelector(
        `script[src^="https://www.google.com/recaptcha/api.js"]`
      );
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [siteKey]);

  const executeRecaptcha = async (action: string): Promise<string> => {
    // If no site key, return empty token (backend will handle based on Enabled flag)
    if (!siteKey) {
      return '';
    }

    // If script failed to load, throw error
    if (loadError) {
      throw new Error(loadError);
    }

    // Wait for script to load
    if (!isLoaded || !window.grecaptcha) {
      throw new Error('reCAPTCHA not loaded yet. Please wait and try again.');
    }

    try {
      const token = await window.grecaptcha.execute(siteKey, { action });
      return token;
    } catch (error) {
      console.error('Error executing reCAPTCHA:', error);
      throw new Error('Failed to generate security token. Please try again.');
    }
  };

  const contextValue: ReCaptchaContextType = {
    isLoaded,
    executeRecaptcha,
  };

  return (
    <ReCaptchaContext.Provider value={contextValue}>
      {children}
    </ReCaptchaContext.Provider>
  );
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}
